import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ControllAppService } from '../../../../../services/controllApp.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';

import { Router } from '@angular/router';

@Component({
  selector: 'app-cadastrar-usuario',
  standalone: false,
  templateUrl: './cadastrar-usuario.component.html',
  styleUrls: ['./cadastrar-usuario.component.css'],
})
export class CadastrarUsuarioComponent implements OnInit, OnDestroy, AfterViewInit {
  fotoUrl: File | null = null;
  fotoPreview: string | ArrayBuffer | null = null;
  empresas: any[] = [];
  carregandoEmpresas: boolean = false;
  erroCarregarEmpresas: string = '';
  currentUserRole: string = '';
  formulario!: FormGroup;

  private readonly STORAGE_KEY = 'cadastro_usuario_form_data';

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('video') video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;

  cameraModalAberto: boolean = false;
  private mediaStream: MediaStream | null = null;
  public perfis = [
    { label: 'Colaborador', value: 'Colaborador' },
    { label: 'Administrador', value: 'Administrador' },
    { label: 'Supervisor', value: 'Supervisor' },
    { label: 'SuperAdministrador', value: 'SuperAdministrador' },
    { label: 'Visitante', value: 'Visitante' }
  ];

  senhaVisivel: boolean = false;
  senhaConfirmacaoVisivel: boolean = false;


  constructor(
    private fb: FormBuilder,
    private controllAppService: ControllAppService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.formulario = this.fb.group(
      {
        nome: ['', [Validators.required, Validators.minLength(8)]],
        userName: ['', [Validators.required, Validators.minLength(4)]],
        cpf: ['', [Validators.pattern(/^\d{11}$/)],
        ],
        email: [''],
        empresaId: ['', [Validators.required]],
        role: ['Colaborador', [Validators.required]],
        senha: ['', [Validators.required]],
        senhaConfirmacao: ['', [Validators.required]],
      },
      { validators: this.senhaConfirmacaoValidator }
    );

    // Recuperar dados salvos do formulário, se existirem
    this.recuperarDadosFormulario();

    // Monitorar mudanças no formulário e salvar
    this.formulario.valueChanges.subscribe(() => {
      this.salvarDadosFormulario();
    });

    this.carregarEmpresas();
    this.getCurrentUserRole(); // Adicionando a chamada ao método
  }

  ngOnDestroy(): void {
    // Não limpamos os dados ao destruir o componente para persistir entre navegações
  }

  ngAfterViewInit(): void {
    // Nada aqui, pois o modal é aberto sob demanda
  }

  toggleSenhaVisivel(): void {
    this.senhaVisivel = !this.senhaVisivel;
  }

  toggleSenhaConfirmacaoVisivel(): void {
    this.senhaConfirmacaoVisivel = !this.senhaConfirmacaoVisivel;
  }


  carregarEmpresas(): void {
    this.carregandoEmpresas = true;
    this.erroCarregarEmpresas = '';

    this.controllAppService.buscarEmpresas().subscribe({
      next: (response) => {
        this.empresas = response;

        this.carregandoEmpresas = false;
      },
      error: (error) => {
        console.error('Erro ao carregar empresas:', error);
        this.erroCarregarEmpresas = 'Não foi possível carregar a lista de empresas. Por favor, tente novamente mais tarde.';
        this.carregandoEmpresas = false;
      }
    });
  }

  get f() {
    return this.formulario.controls;
  }

  private senhaConfirmacaoValidator(control: AbstractControl) {
    const senha = control.get('senha')?.value;
    const confirmacao = control.get('senhaConfirmacao')?.value;
    return senha !== confirmacao ? { senhaDiferente: true } : null;
  }

  abrirCameraModal(): void {
    this.cameraModalAberto = true;
    setTimeout(() => {
      this.iniciarCamera();
    }, 100);
  }

  fecharCameraModal(): void {
    this.cameraModalAberto = false;
    this.pararCamera();
  }

  iniciarCamera(): void {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          this.mediaStream = stream;
          if (this.video && this.video.nativeElement) {
            this.video.nativeElement.srcObject = stream;
            this.video.nativeElement.play();
          }
        })
        .catch(err => {
          Swal.fire({
            title: 'Erro ao acessar câmera',
            text: 'Não foi possível acessar a câmera. Permita o acesso ou escolha um arquivo.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          this.fecharCameraModal();
        });
    }
  }

  pararCamera(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.video && this.video.nativeElement) {
      this.video.nativeElement.srcObject = null;
    }
  }

  capturarFoto(): void {
    if (this.video && this.canvas) {
      const videoEl = this.video.nativeElement;
      const canvasEl = this.canvas.nativeElement;
      canvasEl.width = videoEl.videoWidth || 320;
      canvasEl.height = videoEl.videoHeight || 240;
      const ctx = canvasEl.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoEl, 0, 0, canvasEl.width, canvasEl.height);
        const dataUrl = canvasEl.toDataURL('image/png');
        this.fotoPreview = dataUrl;
        this.fotoUrl = this.dataURLtoFile(dataUrl, 'foto-perfil.png');
        this.fecharCameraModal();
      }
    }
  }

  abrirFilePicker(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
      this.fileInput.nativeElement.click();
    }
  }

  onFileChange(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const tiposPermitidos = ['image/jpeg', 'image/png', 'image/jpg'];
    const extensao = file.name.split('.').pop()?.toLowerCase();

    if (!tiposPermitidos.includes(file.type) || !['jpg', 'jpeg', 'png'].includes(extensao!)) {
      Swal.fire({
        title: 'Formato inválido',
        text: 'Tipo de arquivo inválido. Use JPG, JPEG ou PNG.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (file.size > 2_000_000) {
      Swal.fire({
        title: 'Arquivo muito grande',
        text: 'Imagem muito grande. Use uma com menos de 2MB.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.fotoUrl = file;

    const reader = new FileReader();
    reader.onload = () => (this.fotoPreview = reader.result);
    reader.readAsDataURL(file);

    this.fecharCameraModal();
  }

  // Utilitário para converter dataURL em File
  private dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // Método para salvar dados do formulário no localStorage
  private salvarDadosFormulario(): void {
    if (this.formulario.valid || this.formulario.dirty) {
      const formData = {
        ...this.formulario.value,
        // Não salvamos as senhas por segurança
        senha: '',
        senhaConfirmacao: '',
        // Salvar o preview da imagem se existir
        fotoPreviewUrl: this.fotoPreview ? this.fotoPreview.toString() : null
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(formData));
    }
  }

  // Método para recuperar dados do formulário do localStorage
  private recuperarDadosFormulario(): void {
    const savedData = localStorage.getItem(this.STORAGE_KEY);
    if (savedData) {
      try {
        const formData = JSON.parse(savedData);
        // Preencher o formulário com os dados salvos
        this.formulario.patchValue({
          nome: formData.nome || '',
          userName: formData.userName || '',
          cpf: formData.cpf || '',
          email: formData.email || '',
          empresaId: formData.empresaId || '',
          role: formData.role || 'Colaborador'
          // Não recuperamos as senhas por segurança
        });

        // Restaurar preview da imagem se existir
        if (formData.fotoPreviewUrl) {
          this.fotoPreview = formData.fotoPreviewUrl;
        }
      } catch (error) {
        console.error('Erro ao recuperar dados do formulário:', error);
      }
    }
  }

  // Método para limpar dados do formulário no localStorage
  private limparDadosSalvos(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Botão cancelar
  cancelar(): void {
    Swal.fire({
      title: 'Cancelar cadastro?',
      text: 'Os dados preenchidos serão perdidos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Não, continuar preenchendo'
    }).then((result) => {
      if (result.isConfirmed) {
        this.limparDadosSalvos();
        this.formulario.reset();
        this.fotoPreview = null;
        this.fotoUrl = null;
        this.formulario.patchValue({ role: 'Colaborador' });
        this.router.navigate(['/admin/usuarios']);
      }
    });
  }

  onSubmit(): void {

    if (this.formulario.invalid) {
      Swal.fire({
        title: 'Formulário Inválido',
        text: 'Por favor, preencha todos os campos corretamente.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!this.fotoUrl) {
      Swal.fire({
        title: 'Foto obrigatória',
        text: 'Por favor, envie uma foto de perfil.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    const formData = new FormData();
    formData.append('Nome', this.formulario.value.nome);
    formData.append('UserName', this.formulario.value.userName);
    formData.append('CPF', this.formulario.value.cpf.replace(/\D/g, '')); // Remove non-digits
    formData.append('Email', this.formulario.value.email);
    formData.append('EmpresaId', this.formulario.value.empresaId);
    formData.append('Role', this.formulario.value.role);
    formData.append('Senha', this.formulario.value.senha);

    if (this.fotoUrl) {
      formData.append('FotoFile', this.fotoUrl, this.fotoUrl.name);
    }

    this.controllAppService.register(formData).subscribe({
      next: () => {
        this.limparDadosSalvos(); // Limpar dados ao cadastrar com sucesso
        this.formulario.reset();
        this.fotoPreview = null;
        this.fotoUrl = null;
        this.formulario.patchValue({ role: 'Colaborador' });
      },
      error: (error) => {
        console.error('❌ API registration failed:', error);

        // Display error with SweetAlert
        Swal.fire({
          title: 'Erro no Cadastro',
          text: error.message || 'Ocorreu um erro ao cadastrar o usuário.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });
  }

  // Helper method to debug form errors
  getFormValidationErrors(): string[] {
    const errors: string[] = [];
    Object.keys(this.formulario.controls).forEach(key => {
      const control = this.formulario.get(key);
      if (control?.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          errors.push(`${key}: ${errorKey}`);
        });
      }
    });
    return errors;
  }

  getCurrentUserRole(): void {
    // Acessar o localStorage para buscar os dados do usuário
    const usuario = localStorage.getItem('usuario');
    if (usuario) {
      try {
        const usuarioObj = JSON.parse(usuario);

        // Verificando a estrutura conforme visto na captura de tela
        if (usuarioObj && usuarioObj.role) {
          this.currentUserRole = usuarioObj.role;
        }
      } catch (error) {
        console.error('Erro ao processar dados do usuário:', error);
      }
    } else {
      const keys = Object.keys(localStorage);

      for (const key of keys) {
        if (key.toLowerCase().includes('user') || key.toLowerCase().includes('usuario')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');

            if (data && data.role === "SuperAdministrador") {
              this.currentUserRole = data.role;
              break;
            }
          } catch (error) {
            // Continuar verificando outras chaves
          }
        }
      }
    }

  }
}